/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import {
  BaseButton,
  Button,
  Grid,
  IconButton,
  Modal,
  Table,
  TextGroupMedium,
} from "@cd2/components"
import { FileUploader, useFileboy } from "@cd2/fileboy-react"
import { Fields, Form2 } from "@cd2/forms"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { IWebClient } from "@cd2/skeem-client"
import { H4, P } from "@cd2/text"
import { assert, IComponentProp, titleize } from "@cd2/tools"
import React, { useEffect, useRef, useState } from "react"
import { Link, useHistory } from "react-router-dom"
import { FileboyFilePreviewColumn } from "../NewComponents/Columns"
import { ProductMasterThumbnail } from "../Pages/CustomerArea/components/ProductMasterThumbnail"
import { useContactCreateForCustomerConfirmation } from "../Pages/Customers/CustomerShowContactsPage"
import { fakeID, IRmaFormData } from "../Pages/Rmas/CustomerRmaForm"
import { IRmaItem, RmaRequestType, RmaReturnReason, RmaStatus } from "../Pages/Rmas/RmasIndexPage"
import { ISupportTicket } from "../Pages/SupportTicketsShowPage"
import { buttonConfigs } from "../constants/buttonConfigs"
import { icons } from "../constants/icons"
import { useApi } from "../hooks/useApi"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useConfirmCancelConfirmation } from "../hooks/useConfirmCancelConfirmation"
import { useConfirmation } from "../hooks/useConfirmation"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { useStable } from "../hooks/useStable"
import { HoverCustomerLoader } from "../hoverLoaders/HoverCustomerLoader"
import { HoverOrderLoader } from "../hoverLoaders/HoverOrderLoader"
import { HoverRmaLoader } from "../hoverLoaders/HoverRmaLoader"
import { buildRoute, eRoutes } from "../routes"
import { IFileType } from "../types/common"
import { IRecord } from "../types/schema"
import { ConfirmCancelButtons } from "./Buttons/ConfirmCancelButtons"
import { CreateButton } from "./Buttons/CreateButton"
import { DownloadLinkButton } from "./Buttons/DownloadLink"
import { ButtonsWrapper } from "./ButtonsWrapper"
import { colors, themeColors } from "./EOSThemeProvider"
import { EventList, useEvents } from "./EventsList"
import { FloatingBox } from "./Misc/FloatingBox"
import { ContactSelectList } from "./SelectLists/ContactSelectList"
import { RmaSelect } from "./SelectLists/RmaSelect"
import { LoadingTable } from "./Tables/utils"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { useBetaFeatureAccess } from "./BetaFeatureAccess"
import { CustomerSelectList2, ICustomerSelect2 } from "./SelectLists/CustomerSelectList2"
import { buildCustomerSearchFilter } from "../../shared/search"
import { BranchSelect } from "./SelectLists/BranchSelect"

export const supportTicketStatuses = [
  { label: "Open", value: "open" },
  { label: "Active", value: "active" },
  { label: "Closed", value: "closed" },
] as const
export type ISupportTicketStatus = (typeof supportTicketStatuses)[number]["value"]

interface ISupportTicketsFormProps {
  data?: Omit<Partial<IData>, "files"> & { files: IRecord<"files">[] }
  isEditPage?: boolean
  extraPanel?: React.ReactNode
  setCustomerSelected?: React.Dispatch<React.SetStateAction<ICustomerSelect2 | null>>
  ticketId?: string
  disabled?: boolean

  onSubmit(data: IData, closedMessage?: string): Promise<void>
}

export function SupportTicketsForm({
  setCustomerSelected,
  onSubmit,
  data,
  isEditPage,
  extraPanel,
  ticketId,
  disabled,
}: ISupportTicketsFormProps) {
  const betaFeature = useBetaFeatureAccess()
  const history = useHistory()
  const [handleManageFileNode, handleManageFile] = useSupportTicketFilesManage()
  const formRef = useRef<FormState | null>(null)
  const [reloadHistoryKey, setReloadHistoryKey] = useState(0)
  const events = useEvents()
  const alerts = useSiteAlerts()
  const [contactFormNode, _getContactInformation, handleCreateContactFromNode] =
    useContactCreateForCustomerConfirmation()

  const [addNoteNode, handleAddNote] = useQuickNote()
  const [resolutionLogNode, handleLogResolution] = useTicketClosed()

  const [confirmNewRma, handleConfirmCreateRma] = useConfirmCancelConfirmation({
    heading: "Create new RMA?",
    body: "Are you sure you want to create a new RMA? This will attempt to build an RMA using the data supplied in this support ticket",
  })

  const [customerOption, setCustomerOption] = useState<ICustomerSelect2 | null>(null)
  useAsyncEffect(async () => {
    if (data?.customer) {
      const customerData = await window.skeem.fetch2<ICustomerSelect2>({
        first: {
          name: "customers",
          attributes: CustomerSelectList2.attributes,
          filter: {
            matches: { id: data?.customer },
          },
        },
      })
      setCustomerOption(customerData)
    }
  }, [])

  useEffect(() => {
    if (data?.assigned_to_user && formRef.current) {
      formRef.current.setPartialData({ assigned_to_user: data.assigned_to_user })
    }
  }, [data?.assigned_to_user])

  async function handleQuickAddNote() {
    if (!data?.id) {
      return
    }
    const message = await handleAddNote()
    if (!message) {
      return
    }
    await events.addNotes("support_tickets", data.id, { message })
    setReloadHistoryKey(k => k + 1)
    alerts.genericSuccess("Note added")
  }

  async function handleCreateContact(customer: string) {
    assert(customer)
    const customerRecord = await window.skeem.fetch2<
      IRecord<"customers", "name" | "isGenericCustomer"> & { branch: IRecord<"branches"> }
    >({
      first: {
        name: "customers",
        attributes: ["name", "branch", "isGenericCustomer"],
        filter: { matches: { id: customer } },
      },
    })
    try {
      const contact = await handleCreateContactFromNode(customerRecord)
      if (contact) {
        return contact.id
      }
    } catch (e) {
      alerts.genericError(JSON.stringify(e))
    }
  }

  async function handleSubmit(formData: IData) {
    if (data?.id && formData.status === "closed" && data.status !== "closed") {
      const message = await handleLogResolution(data.resolution)
      if (message === null) {
        return
      }
      await onSubmit(formData, message ?? undefined)
    } else {
      await onSubmit(formData)
    }
    setReloadHistoryKey(k => k + 1)
  }

  const handleNewRMA = useAsyncCallback(async (formData: IData) => {
    if (
      !confirm(
        "You will be redirected to the RMA form.\nUnsaved changes will be lost.\nAre you sure you want to continue?",
      )
    ) {
      return
    }
    const result = await handleCreateRMAFromTicket(formData)
    result.supportTicket = { id: ticketId!, identifier: "" }
    history.push({ pathname: eRoutes["/customer-support/rma/new"], state: result })
  })

  return (
    <React.Fragment>
      {confirmNewRma}
      {addNoteNode}
      {resolutionLogNode}
      {contactFormNode}
      {handleManageFileNode}
      <Form2.Form
        xRef={formRef}
        onSubmit={handleSubmit}
        initialData={{ ...data, status: data?.status || "open" }}
      >
        <div>
          <div
            css={css`
              padding: 0 12px 12px;
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <Form2.WithForm
              render={form => (
                <BaseButton
                  {...buttonConfigs.formSaveButton}
                  disabled={form.state.isSubmitting || disabled}
                />
              )}
              subscribe={{ dataChange: true }}
            />

            {data?.id && (
              <Form2.WithForm
                render={form => (
                  <BaseButton
                    {...buttonConfigs.create}
                    children="Create RMA"
                    disabled={handleNewRMA.running || disabled}
                    onClick={() => handleNewRMA(form.getData() as IData)}
                  />
                )}
              />
            )}
          </div>
          <Grid
            columns={[{ fr: 1 }, "max-content", 200]}
            gap={8}
            css={css`
              background-color: #e5e5e5;
              padding: 8px 16px;
              justify-content: end;
            `}
          >
            <Form2.WithForm
              subscribe={{ dataChange: true }}
              render={form => {
                const formData = form.getData() as IData
                const branch = form.getField("branch").getValue()

                return (
                  <Grid gap={8} columns={[200, 200, "max-content"]} alignItems="start">
                    <Form2.WithField<string>
                      field="branch"
                      render={() => (
                        <BranchSelect
                          label="Branch"
                          onChange={value => {
                            // determine if we need to clear customer/contact because of branch change
                            const isBranchChanging = formData.branch !== value?.id
                            const mustChangeCustomer = isBranchChanging

                            form.setPartialData({
                              branch: value?.id,
                              customer: mustChangeCustomer ? null : formData.customer,
                              contact: mustChangeCustomer ? null : formData.contact,
                            })
                            if (mustChangeCustomer) {
                              setCustomerOption(null)
                            }
                          }}
                          value={branch ? { id: branch, name: "" } : undefined}
                          skeemFilter={{
                            and: [
                              formData.customer && {
                                exists: {
                                  path: [
                                    {
                                      name: "customers",
                                      filter: { matches: { id: formData.customer } },
                                    },
                                  ],
                                },
                              },
                            ].filter(Boolean),
                          }}
                          disabled={
                            (!!data?.customer && isEditPage) || !!formData.contact || disabled
                          }
                          isClearable={!((!!data?.customer && isEditPage) || !!formData.contact)}
                        />
                      )}
                    />

                    <CustomerSelectList2
                      key={[branch, customerOption?.id, "customer-select"].join("-")}
                      label="Customer"
                      validations={{ presence: true }}
                      onChange={(value, opt) => {
                        setCustomerSelected?.(opt ?? null)
                        setCustomerOption(opt ?? null)
                        if (opt) {
                          form.setPartialData({
                            customer: value,
                            branch: opt.branch?.id,
                          })
                        } else {
                          // don't clear the branch when clearing customer
                          form.setPartialData({ customer: value, contact: undefined })
                        }
                      }}
                      value={customerOption}
                      allBranches={!branch}
                      includeBranchName={!branch}
                      branchId={branch ?? undefined}
                      searchFilter={(query: string) => ({
                        or: [
                          buildCustomerSearchFilter(query),
                          {
                            exists: {
                              path: [
                                {
                                  name: "addresses",
                                  filter: {
                                    and: [
                                      {
                                        matches: {
                                          showInCustomerAddressOptions: true,
                                        },
                                      },
                                      {
                                        like: [
                                          {
                                            stringReplace: [{ attr: "postcode" }, " ", ""],
                                          },
                                          { value: query.replace(/\s/g, "") },
                                        ],
                                      },
                                    ],
                                  },
                                },
                              ],
                            },
                          },
                        ],
                      })}
                      disabled={(!!data?.customer && isEditPage) || !!formData.contact || disabled}
                      isClearable={!((!!data?.customer && isEditPage) || !!formData.contact)}
                      includeLastOrderDate
                    />

                    {formData.customer ? (
                      <Grid columns={[200, "max-content"]} gap={8} alignItems="end">
                        <ContactSelectList
                          key={[branch, customerOption?.id, "customer-select"].join("-")}
                          value={formData.contact ?? undefined}
                          customerId={formData.customer}
                          onChange={value => form.getField("contact").setValue(value)}
                          skeemFilter={{ not: { matches: { email: "" } } }}
                          branchId={formData.branch ?? undefined}
                          disabled={disabled}
                          isClearable
                        />
                        <div>
                          <CreateButton
                            onClick={() => {
                              handleCreateContact(formData.customer!).then(result => {
                                if (result) {
                                  form.getField("contact").setValue(result)
                                }
                              })
                            }}
                            buttonStyle="text"
                            extendTheme={{ padding: 8, fontSize: 12 }}
                            disabled={disabled}
                            color="#666"
                          >
                            Create new contact
                          </CreateButton>
                        </div>
                      </Grid>
                    ) : (
                      <div
                        css={css`
                          width: 200px;
                        `}
                      >
                        <Fields.Select options={[]} label="Contact" disabled />
                      </div>
                    )}
                  </Grid>
                )
              }}
            />
            <div
              css={css`
                display: flex;
                align-items: flex-end;
              `}
            >
              {data?.id && (
                <Button
                  leftIcon={icons.plus}
                  onClick={handleQuickAddNote}
                  buttonColor="white"
                  disabled={disabled}
                >
                  Make a Note
                </Button>
              )}
            </div>
            <Form2.Select
              field="status"
              label="Status"
              options={supportTicketStatuses}
              valueExtractor={(opt: { value: ISupportTicketStatus }) => opt.value}
              getOptionLabel={opt => opt.label}
              getOptionValue={opt => opt.value}
              validations={{ presence: true }}
              disabled={disabled}
            />
          </Grid>

          <Grid
            columns={[{ fr: 2 }, { fr: 4 }, { fr: 2 }]}
            css={css`
              border-top: 2px solid #ddd;
              border-bottom: 2px solid #ddd;
            `}
          >
            <div
              css={css`
                background-color: #f5f5f5;
                padding: 12px;
              `}
            >
              <Grid gap={8}>
                <FloatingBox header={<H4>Assigned to</H4>}>
                  <Grid gap={8}>
                    <Form2ModelSelect
                      key={data?.assigned_to_user ?? "assigned_to_user"}
                      label="Support Rep"
                      model="skeem_users"
                      field="assigned_to_user"
                      labelAttribute="name"
                      filter={{ matches: { userAccountType: "branch" } }}
                      disabled={disabled}
                      isClearable
                    />
                    <Form2ModelSelect
                      label="Team"
                      model="support_ticket_groups"
                      field="assigned_to_group"
                      labelAttribute="name"
                      disabled={disabled}
                      isClearable
                    />
                    <Form2.WithField<{ id: string } | undefined>
                      field="rmas"
                      render={field => (
                        <RmaSelect
                          label="RMA"
                          onChange={(_, rma) => field.setValue(rma)}
                          value={field.getValue()?.id}
                          branchId={data?.branch ?? undefined}
                          customerId={data?.customer ?? undefined}
                          error={field.getError()}
                          isClearable
                        />
                      )}
                    />
                  </Grid>
                </FloatingBox>
                {extraPanel}
                {data?.id && (
                  <FloatingBox header={<H4>Activity</H4>}>
                    <EventList
                      key={reloadHistoryKey}
                      model="support_tickets"
                      id={data.id}
                      limit={5}
                      viewAllLink={buildRoute(
                        eRoutes["/customer-support/support-tickets/:id/history"],
                        { id: data.id },
                      )}
                    />
                  </FloatingBox>
                )}
                {data?.rmas && <HoverRmaLoader id={data.rmas.id} inline />}
              </Grid>
            </div>
            <div
              css={css`
                background-color: white;
                border-left: 1px solid #ddd;
                border-right: 1px solid #ddd;
                padding: 16px;
              `}
            >
              <Grid gap={8}>
                <FloatingBox header={<H4>Details</H4>}>
                  <Grid gap={8}>
                    <Grid gap={8} columns={[{ fr: 3 }, { fr: 1 }]}>
                      <Form2.Text
                        field="title"
                        validations={{ presence: true }}
                        disabled={disabled}
                      />
                      <Form2TextOptionSelect
                        options={["high", "medium", "low"]}
                        field="priority"
                        validations={{ presence: true }}
                        disabled={disabled}
                      />
                    </Grid>
                    <div
                      css={css`
                        ${disabled ? "opacity: 0.5;" : ""}
                      `}
                    >
                      <Form2.TextArea
                        field="details"
                        validations={{ presence: true }}
                        rows="auto"
                        // disabled={disabled}
                        inputProps={{ disabled: disabled }}
                      />
                    </div>
                    <Grid gap={8} columns={[{ fr: 1 }, { fr: 1 }]}>
                      <Form2ModelSelect
                        model="support_ticket_types"
                        field="type"
                        labelAttribute="name"
                        disabled={disabled}
                        isClearable
                      />
                      <Form2ModelSelect
                        model="support_ticket_sources"
                        field="source"
                        labelAttribute="name"
                        disabled={disabled}
                        isClearable
                      />
                    </Grid>
                    <div>
                      <Form2.Text
                        field="custom_product_code"
                        postFieldText={
                          <P size="small">Product code not on the system? You can enter it here.</P>
                        }
                        disabled={disabled}
                      />
                    </div>
                  </Grid>
                </FloatingBox>
                <FloatingBox header={<H4>Product</H4>} color={themeColors.lightestGrey}>
                  <ResourceSelectors />
                </FloatingBox>
                <Form2.WithField
                  field="files"
                  render={field => (
                    <SupportTicketFilesManager
                      ticketId={ticketId}
                      onFilesChange={field.setValue}
                      handleFile={handleManageFile}
                      dependencies={data?.files?.map(v => v.id) ?? []}
                      disabled={disabled}
                    />
                  )}
                />
              </Grid>
            </div>
            <div
              css={css`
                background-color: #f5f5f5;
                padding: 12px;
              `}
            >
              <Grid gap={8}>
                <Form2.WithForm
                  subscribe={{ dataChange: true }}
                  render={form => {
                    const data = form.getData() as IData
                    return (
                      <React.Fragment>
                        {data.customer && (
                          <HoverCustomerLoader
                            id={data.customer}
                            inline
                            includeEventList
                            startExpanded
                          />
                        )}
                        {data.order && <HoverOrderLoader id={data.order} inline />}
                      </React.Fragment>
                    )
                  }}
                />
              </Grid>
            </div>
          </Grid>
        </div>
        <div
          css={css`
            padding-left: 12px;
          `}
        >
          <Form2.WithForm
            render={form => (
              <BaseButton
                {...buttonConfigs.formSaveButton}
                disabled={form.state.isSubmitting || disabled}
              />
            )}
            subscribe={{ submitting: true }}
          />
        </div>
      </Form2.Form>
    </React.Fragment>
  )
}

type IFile = IRecord<"files", "fileName" | "fileboyId"> & {
  fileType: IFileType
}
type ITemporaryFile = (
  | (IFile & { key: string })
  | (Omit<IFile, "id"> & { id?: string; key: string })
) & { _delete?: boolean }

function FormFileFieldValidations() {
  Form2.useFormSubmitLock(formState => {
    const idField = formState.getField("fileboyId")
    if (!idField.getValue()) {
      idField.setError("must be present")
      return false
    } else {
      idField.setError(undefined)
    }
  })
  return null
}

function SupportTicketFilesManager({
  ticketId,
  onFilesChange,
  handleFile,
  dependencies,
  disabled,
}: {
  ticketId?: string
  onFilesChange: (files: ITemporaryFile[]) => void
  handleFile: ReturnType<typeof useSupportTicketFilesManage>[1]
  dependencies: string[]
  disabled?: boolean
}) {
  const alerts = useSiteAlerts()
  const skeem = window.skeem
  const [files, setFiles] = useState<ITemporaryFile[] | null>()

  async function handleManageFile(initData: ITemporaryFile | { fileType: IFileType }) {
    const data = await handleFile(initData)
    if (!data) {
      return
    }
    if ("key" in data) {
      setFiles(old => (old ?? []).map(fileData => (fileData.key === data.key ? data : fileData)))
      alerts.genericSuccess(`${titleize(initData.fileType)} updated`)
    } else {
      setFiles(old => [...(old ?? []), { ...data, key: Math.random().toString() }])
      alerts.genericSuccess(`${titleize(initData.fileType)} added`)
    }
  }

  useEffect(() => {
    onFilesChange?.(files ?? [])
  }, [JSON.stringify(files)])

  function handleRemoveFile(file: ITemporaryFile) {
    setFiles(old =>
      (old ?? []).map(oFile =>
        oFile.key === file.key ? { ...oFile, _delete: !oFile._delete } : oFile,
      ),
    )
  }

  async function fetchFiles() {
    const files = await skeem.fetch2<IFile[]>({
      model: {
        name: "files",
        attributes: ["fileName", "fileboyId", "fileType"],
        filter: { matches: { support_ticket: ticketId } },
      },
    })
    setFiles(files.map(file => ({ ...file, key: file.id })))
  }

  useAsyncEffect(async () => {
    if (!ticketId) {
      setFiles([])
      return
    }
    await fetchFiles()
  }, [ticketId, JSON.stringify(dependencies)])
  const fileboy = useFileboy()

  return (
    <FloatingBox
      header={<H4>Files</H4>}
      footer={
        <ButtonsWrapper>
          <CreateButton onClick={() => handleManageFile({ fileType: "file" })} disabled={disabled}>
            Add File
          </CreateButton>
        </ButtonsWrapper>
      }
    >
      {files === null ? (
        <LoadingTable />
      ) : files?.length === 0 ? (
        <P color="#999" align="center">
          Files attached to this ticket will appear here.
        </P>
      ) : (
        <Table.HorizontalOverflow minWidth={600}>
          <Table data={files ?? []}>
            <FileboyFilePreviewColumn field="fileboyId" />
            <Table.DataColumn field="fileName" heading="Name" />
            <Table.Column>
              {row => (
                <DownloadLinkButton
                  to={fileboy.buildFileUrl(row.fileboyId, {
                    download: true,
                    filename: row.fileName,
                  })}
                  extendTheme={{ padding: { topAndBottom: 8, sides: 12 } }}
                >
                  Download
                </DownloadLinkButton>
              )}
            </Table.Column>
            <Table.Column
              css={css`
                width: 1px;
              `}
            >
              {row => (
                <Grid columns={["max-content", "max-content"]} gap={8}>
                  <IconButton
                    icon={icons.edit}
                    onClick={() => handleManageFile(row)}
                    disabled={row._delete || disabled}
                    buttonStyle="text"
                  />
                  <IconButton
                    icon={row._delete ? icons.refresh : icons.delete}
                    buttonColor={row._delete ? "green" : "red"}
                    onClick={() => handleRemoveFile(row)}
                    buttonStyle="text"
                    disabled={disabled}
                  />
                </Grid>
              )}
            </Table.Column>
          </Table>
        </Table.HorizontalOverflow>
      )}
    </FloatingBox>
  )
}

function useSupportTicketFilesManage() {
  return useConfirmation<
    ITemporaryFile | { fileType: IFileType; id?: never },
    ITemporaryFile | Omit<ITemporaryFile, "key"> | null
  >(({ data, onResolve }) => (
    <Modal
      render={() => (
        <div>
          <TextGroupMedium
            heading={data.id ? `Edit ${titleize(data.fileType)}` : `Add ${titleize(data.fileType)}`}
          />
          <Form2.Form initialData={data} onSubmit={onResolve}>
            <Form2.Text field="fileName" validations={{ presence: true }} />
            <FormFileFieldValidations />
            <Form2.WithForm
              subscribe={{ dataChange: true, submitting: true }}
              render={form => (
                <Grid gap={4}>
                  <div>
                    <Form2.FieldLabel
                      fieldName="fileboyId"
                      label={titleize(data.fileType)}
                      required
                    />
                  </div>
                  <FileUploader
                    onFileUploaded={file => {
                      const data: Record<string, string> = { fileboyId: file.id }
                      const fileNameField = form.getField("fileName")
                      if (!fileNameField.getValue()) {
                        data.fileName = file.originalFilename
                      }
                      form.setPartialData(data)
                    }}
                    defaultFileId={form.getField("fileboyId").getValue()}
                  />
                  {form.getField("fileboyId").getError() && (
                    <P color="error" size="small">
                      {form.getField("fileboyId").getError()}
                    </P>
                  )}
                </Grid>
              )}
            />
            <ConfirmCancelButtons
              onCancel={() => onResolve(null)}
              onConfirm="submit"
              buttonOpts={{ confirm: { children: "Submit" } }}
            />
          </Form2.Form>
        </div>
      )}
      isOpen
    />
  ))
}

export function SupportTicketStatusSelect({
  onChange,
  value,
}: {
  onChange: (value?: ISupportTicketStatus) => void
  value?: ISupportTicketStatus
}) {
  return (
    <Fields.Select2
      onChange={value => onChange(value?.value) as any}
      value={supportTicketStatuses.find(v => v.value === value)}
      label="Status"
      options={supportTicketStatuses}
      getOptionLabel={opt => opt.label}
      getOptionValue={opt => opt.value}
      isClearable
    />
  )
}

type IProductMaster = IRecord<"product_masters", "stock_code" | "name">

interface IFetchData {
  product_master: IProductMaster | null
  order: IRecord<"orders", "identifier"> | null
  order_item: IRecord<"order_items", "stock_code" | "quantity" | "body"> | null
}

function ResourceSelectors() {
  const skeem = window.skeem
  const form = Form2.useForm({ subscribe: { dataChange: true } })
  const [customerResourcesOnly, setCustomerResourcesOnly] = useState(true)

  const [loadedRecords, setLoadedRecords] = useState<IFetchData | null>(null)
  const clearedRef = useRef(true)
  const loadRef = useRef(0)
  const timeoutRef = useRef<any>()

  const data = form.getData() as Partial<IData>

  useAsyncEffect(async () => {
    const key = Math.random()
    loadRef.current = key
    const setData = {} as any
    const product_master = await loadProduct()
    setData.product_master = product_master
    if (customerResourcesOnly) {
      const order = await loadOrder()
      setData.order = order
      const order_item = await loadOrderItem()
      setData.order_item = order_item
    }
    if (loadRef.current === key) {
      setLoadedRecords(setData)
      // timeout delays the auto reselct re-enabling giving a bigger time to clear things before it
      // starts auto selecting again.
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => (clearedRef.current = false), 250)
    }
  }, [data.order, data.product_master, data.order_item])

  useAsyncEffect(async () => {
    if ((data.product_master || data.order) && data.customer) {
      const record = await skeem.fetch2<
        { product_master: { id: string }[]; order: { id: string }[] } | undefined
      >({
        obj: {
          product_master: {
            model: {
              name: "product_masters",
              filter: {
                and: [
                  {
                    exists: {
                      path: [
                        "products",
                        "order_items",
                        "order",
                        { name: "customer", filter: { matches: { id: customer } } },
                      ],
                    },
                  },
                  { matches: { id: data.product_master } },
                ],
              },
            },
          },
          order: {
            model: {
              name: "orders",
              filter: {
                and: [
                  {
                    exists: {
                      path: [{ name: "customer", filter: { matches: { id: customer } } }],
                    },
                  },
                  { matches: { id: data.order } },
                ],
              },
            },
          },
        },
      })
      if (record?.order.length || record?.product_master.length) {
        setCustomerResourcesOnly(true)
      } else {
        setCustomerResourcesOnly(false)
      }
    }
  }, [])

  function loadProduct() {
    if (!data.product_master) {
      return null
    }
    return skeem.fetch2<IFetchData["product_master"]>({
      first: {
        name: "product_masters",
        attributes: ["stock_code", "name"],
        filter: { matches: { id: data.product_master } },
      },
    })
  }

  function loadOrder() {
    if (!data.order) {
      return null
    }
    return skeem.fetch2<IFetchData["order"]>({
      first: {
        name: "orders",
        attributes: ["identifier"],
        filter: { matches: { id: data.order } },
      },
    })
  }

  function loadOrderItem() {
    if (!data.order_item) {
      return null
    }
    return skeem.fetch2<IFetchData["order_item"]>({
      first: {
        name: "order_items",
        attributes: ["quantity", "body", "stock_code"],
        filter: { matches: { id: data.order_item } },
      },
    })
  }

  const itemCss = css`
    padding: 8px;
    border: 1px solid ${colors.lightBorder};
    border-radius: 3px;
    background-color: white;
  `

  const resetCss = css`
    cursor: pointer;
  `

  if (!data.customer) {
    return (
      <div>
        <P>Select a customer to add resources to this ticket</P>
      </div>
    )
  }

  const customer = data.customer

  const hasData = [data.order, data.product_master].some(Boolean)

  return (
    <Grid gap={8}>
      <Grid columns={["auto", "max-content"]} gap={8}>
        <div>
          <P>
            <span
              css={css`
                cursor: pointer;
                text-decoration: 1px dashed underline ${colors.lightBorder};
                text-underline-offset: 2px;
              `}
              onClick={() => {
                form.setPartialData({
                  order: undefined,
                  order_item: undefined,
                })
                setCustomerResourcesOnly(k => !k)
              }}
            >
              Select from {!customerResourcesOnly ? "this customers records" : "all products"}
            </span>
          </P>
        </div>
        <div
          css={css`
            width: max-content;
          `}
        >
          {hasData && (
            <a
              css={resetCss}
              onClick={() => {
                clearedRef.current = true
                form.setPartialData({
                  order: undefined,
                  product_master: undefined,
                  order_item: undefined,
                })
              }}
            >
              clear all
            </a>
          )}
        </div>
      </Grid>
      {!customerResourcesOnly ? null : (
        <Form2.Text field="serialNumberText" label="Serial Number" />
      )}
      {!data.product_master ? (
        customerResourcesOnly ? (
          <ProductMasterSelect
            customer={customer}
            order={data.order ?? undefined}
            order_item={data.order_item ?? undefined}
            useAutoSelect={!clearedRef.current}
          />
        ) : (
          <ProductMasterAsyncSelect />
        )
      ) : (
        <div css={itemCss}>
          <Grid columns={["auto", "max-content"]} gap={8}>
            <H4>Product</H4>
            <a
              css={resetCss}
              onClick={() => {
                clearedRef.current = true
                form.setPartialData({ product_master: undefined })
              }}
            >
              clear
            </a>
          </Grid>
          <Grid columns={[40, "auto"]} gap={16} alignItems="center">
            <ProductMasterThumbnail productMasterId={loadedRecords?.product_master?.id} />
            <div>
              <P>{loadedRecords?.product_master?.stock_code ?? "-"}</P>
              {loadedRecords?.product_master?.name && <P>{loadedRecords?.product_master?.name}</P>}
              {loadedRecords?.product_master && (
                <Link
                  target="_blank"
                  to={buildRoute(eRoutes["/stock/product-masters/:id"], {
                    id: loadedRecords.product_master.id,
                  })}
                >
                  View Product
                </Link>
              )}
            </div>
          </Grid>
        </div>
      )}
      {!customerResourcesOnly ? null : !data.order ? (
        <OrderSelect
          customer={customer}
          product_master={data.product_master ?? undefined}
          useAutoSelect={!clearedRef.current}
        />
      ) : (
        <div css={itemCss}>
          <Grid columns={["auto", "max-content"]} gap={8}>
            <H4>Order</H4>
            <a
              css={resetCss}
              onClick={() => {
                clearedRef.current = true
                form.setPartialData({ order: undefined, order_item: undefined })
              }}
            >
              clear
            </a>
          </Grid>
          <P>{loadedRecords?.order?.identifier ?? "-"}</P>
        </div>
      )}
      {!customerResourcesOnly ? null : !data.order ? null : !data.order_item ? (
        <OrderItemSelect
          customer={customer}
          product_master={data.product_master ?? undefined}
          order={data.order ?? undefined}
          useAutoSelect={!clearedRef.current}
        />
      ) : (
        <div css={itemCss}>
          <Grid columns={["auto", "max-content"]} gap={8}>
            <H4>Order Item</H4>
            <a
              css={resetCss}
              onClick={() => {
                clearedRef.current = true
                form.setPartialData({ order_item: undefined })
              }}
            >
              clear
            </a>
          </Grid>
          <P>{loadedRecords?.order_item?.stock_code ?? "-"}</P>
          <P>{loadedRecords?.order_item?.body ?? "-"}</P>
        </div>
      )}
      <div />
      {customerResourcesOnly && (
        <P size="small" blockSpacing={0}>
          Fields are automatically filled when there is only one possible option based on the
          combination of the selected items.
        </P>
      )}
      <P size="small">
        You can remove selections by clicking on {'"clear"'} on the relevant item or remove all
        selections by clicking on {'"clear all"'}.
      </P>
    </Grid>
  )
}

interface IResourceSelectOpts {
  customer: string
  product_master?: string
  order?: string
  order_item?: string
  useAutoSelect?: boolean
  isClearable?: boolean
}

function formatProductOption(option: IProductMaster) {
  return (
    <Grid columns={[40, "auto"]} gap={16}>
      <div>
        <ProductMasterThumbnail productMasterId={option?.id} />
      </div>
      <div>
        <P blockSpacing={0}>{option.stock_code}</P>
        <P blockSpacing={0} size="small">
          {option.name}
        </P>
        <Link
          onClick={event => event.stopPropagation()}
          target="_blank"
          to={buildRoute(eRoutes["/stock/product-masters/:id"], { id: option.id })}
        >
          View Product
        </Link>
      </div>
    </Grid>
  )
}

function ProductMasterSelect({
  customer,
  order,
  order_item,
  product_master,
  useAutoSelect,
  isClearable,
}: IResourceSelectOpts) {
  const field = Form2.useField("product_master", { subscribe: true })
  const skeem = window.skeem
  const [records, setRecords] = useState<IProductMaster[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const loadRef = useRef(0)

  const stable = useStable({ customer, order_item, order })

  useAsyncEffect(async () => {
    setIsLoading(true)
    try {
      const key = Math.random()
      loadRef.current = key
      const customerRecord = await skeem.fetch2<
        IRecord<"customers"> & {
          productMasterIds: string[]
        }
      >({
        first: {
          name: "customers",
          filter: { matches: { id: customer } },
          attributes: [
            {
              as: "productMasterIds",
              value: {
                path: [
                  { name: "orders", filter: order ? { matches: { id: order } } : undefined },
                  {
                    name: "items",
                    filter: order_item ? { matches: { id: order_item } } : undefined,
                  },
                  // Have to navigate to product -> serial number -> product to filter by serial number
                  "product",
                  {
                    name: "product_master",
                    filter: product_master ? { matches: { id: product_master } } : undefined,
                  },
                  "id",
                ],
              },
            },
          ],
        },
      })
      const data = await skeem.fetch2<IProductMaster[]>({
        model: {
          name: "product_masters",
          attributes: ["name", "stock_code"],
          filter: { matches: { id: customerRecord.productMasterIds } },
        },
      })
      if (loadRef.current === key) {
        setRecords(data)

        if (data.length === 1 && useAutoSelect && order_item) {
          if (field.getValue() !== data[0].id) {
            field.setValue(data[0].id)
            field.triggerChange()
          }
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [JSON.stringify(stable)])

  return (
    <Form2.Select
      field="product_master"
      label="Product"
      getOptionLabel={opt => opt.stock_code}
      getOptionValue={opt => opt.id}
      valueExtractor={(opt: { id: string }) => opt?.id}
      formatOptionLabel={formatProductOption}
      isLoading={isLoading}
      options={records}
      isClearable={isClearable}
    />
  )
}

function ProductMasterAsyncSelect() {
  const skeem = window.skeem
  const [options, setOptions] = useState<IProductMaster[]>()
  const [query, setQuery] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const stableQuery = useStable(query)

  async function loadProductMasters(query: string) {
    const data = await skeem.fetch2<IProductMaster[]>({
      model: {
        name: "product_masters",
        attributes: ["stock_code", "name"],
        paginate: { size: 20 },
        filter: {
          or: [
            { matches: { stock_code: { like: query } } },
            { matches: { name: { like: query } } },
          ],
        },
      },
    })
    return data
  }

  useAsyncEffect(async () => {
    try {
      setIsLoading(true)
      const records = await loadProductMasters(stableQuery)
      setOptions(records)
    } finally {
      setIsLoading(false)
    }
  }, [stableQuery])

  return (
    <Form2.Select
      field="product_master"
      label="Product"
      onInputChange={setQuery}
      inputValue={query}
      getOptionLabel={opt => opt.stock_code}
      getOptionValue={opt => opt.id}
      valueExtractor={(opt: { id: string }) => opt?.id}
      options={options}
      filterOption={() => true}
      formatOptionLabel={formatProductOption}
      isLoading={isLoading}
    />
  )
}

type IOrderData = IRecord<"orders", "identifier" | "sage_order_number"> & {
  customer?: IRecord<"customers", "name"> & { branch?: IRecord<"branches", "name"> }
}

function OrderSelect({
  customer,
  product_master,
  order,
  useAutoSelect,
  isClearable,
}: IResourceSelectOpts) {
  const field = Form2.useField("order", { subscribe: true })
  const skeem = window.skeem
  const [records, setRecords] = useState<IOrderData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const loadRef = useRef(0)

  const stable = useStable({ customer, product_master })

  useAsyncEffect(async () => {
    setIsLoading(true)
    try {
      const key = Math.random()
      loadRef.current = key
      const data = await skeem.fetch2<typeof records>({
        model: {
          name: "orders",
          attributes: [
            "identifier",
            "sage_order_number",
            { name: "customer", attributes: ["name", { name: "branch", attributes: ["name"] }] },
          ],
          sort: { by: "identifier", direction: "desc" },
          filter: {
            and: [
              customer && { matches: { customer } },
              product_master && {
                exists: {
                  path: [
                    "items",
                    "product",
                    { name: "product_master", filter: { matches: { id: product_master } } },
                  ],
                },
              },
              order && { matches: { id: order } },
            ].filter(Boolean),
          },
        },
      })

      if (loadRef.current === key) {
        setRecords(data)
        if (data.length === 1 && useAutoSelect && product_master) {
          if (field.getValue() !== data[0].id) {
            field.setValue(data[0].id)
            field.triggerChange()
          }
        }
      }
    } finally {
      setIsLoading(false)
    }
  }, [JSON.stringify(stable)])

  return (
    <Form2.Select
      field="order"
      label="Order"
      getOptionLabel={opt =>
        `Fortus Live ID: ${opt.identifier}; Sage Order Number: ${opt.sage_order_number}`
      }
      getOptionValue={opt => opt.id}
      valueExtractor={(opt: { id: string }) => opt?.id}
      isLoading={isLoading}
      options={records}
      isClearable={isClearable}
    />
  )
}

type IOrderItemData = IRecord<"order_items", "stock_code" | "quantity" | "body">

function OrderItemSelect({
  customer,
  product_master,
  order,
  useAutoSelect,
  isClearable,
}: IResourceSelectOpts) {
  const field = Form2.useField<string>("order_item", { subscribe: true })
  const skeem = window.skeem
  const [records, setRecords] = useState<IOrderItemData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const loadRef = useRef(0)

  const stable = useStable({
    customer,
    product_master,
    order,
  })

  useAsyncEffect(async () => {
    setIsLoading(true)
    try {
      const key = Math.random()
      loadRef.current = key
      const data = await skeem.fetch2<typeof records>({
        model: {
          name: "order_items",
          attributes: ["stock_code", "quantity", "body"],
          filter: {
            and: [
              customer && {
                exists: {
                  path: ["order", { name: "customer", filter: { matches: { id: customer } } }],
                },
              },
              order && { matches: { order } },
            ].filter(Boolean),
          },
        },
      })

      if (loadRef.current === key) {
        setRecords(data)
        if (data.length === 1 && useAutoSelect && product_master && order) {
          if (data.length === 1 && field.getValue() !== data[0].id) {
            field.setValue(data[0].id)
            field.triggerChange()
          }
        }
      }
    } finally {
      setIsLoading(false)
    }
  }, [JSON.stringify(stable)])

  return (
    <Fields.Select2
      label="Order Item"
      onChange={value => field.setValue(value?.id)}
      value={records.find(item => field.getValue() === item.id)}
      getOptionLabel={opt => opt.stock_code}
      getOptionValue={opt => opt.id}
      valueExtractor={(opt: { id: string }) => opt?.id}
      isLoading={isLoading}
      options={records}
      isClearable={isClearable}
      formatOptionLabel={opt => (
        <div>
          <P blockSpacing={0} size="small">
            {opt.stock_code}
          </P>
          <P blockSpacing={0}>{opt.body}</P>
          <P blockSpacing={0} size="small">
            Qty: {opt.quantity}
          </P>
        </div>
      )}
    />
  )
}

interface ISimpleSelectProps {
  options: string[] | readonly string[]
  isClearable?: boolean
  field: string
  label?: IComponentProp<typeof Form2.Select, "label">
  validations?: IComponentProp<typeof Form2.Select, "validations">
  disabled?: boolean
}

export function Form2TextOptionSelect({
  options,
  isClearable = true,
  field,
  label,
  validations,
  disabled,
}: ISimpleSelectProps) {
  return (
    <Form2.Select
      field={field}
      label={label}
      validations={validations}
      options={(options as string[]).map(opt => ({ label: titleize(opt), value: opt }))}
      valueExtractor={(opt?: { label: string; value: string }) => opt?.value ?? undefined}
      isClearable={isClearable}
      disabled={disabled}
      defaultValue=""
    />
  )
}

interface IForm2ModelSelectProps<RecordType extends Record<string, any>> {
  field: string
  label?: IComponentProp<typeof Form2.Select, "label">
  model: string
  labelAttribute: string
  extraAttributes?: any[]
  validations?: IComponentProp<typeof Form2.Select, "validations">
  filter?: Record<string, any>
  disabled?: boolean
  isClearable?: boolean
  formatOptionLabel?: (item: RecordType) => React.ReactNode
}

export function Form2ModelSelect<RecordType extends Record<string, any>>({
  field,
  label,
  model,
  labelAttribute,
  validations,
  filter,
  disabled,
  isClearable,
  extraAttributes,
  formatOptionLabel,
}: IForm2ModelSelectProps<RecordType>) {
  const skeem = window.skeem
  const [options, setOptions] = useState<any[] | null>()

  useAsyncEffect(async () => {
    await fetchData()
  }, [JSON.stringify(filter), field, model, labelAttribute])

  async function fetchData() {
    const data = await skeem.fetch2<RecordType[]>({
      model: { name: model, attributes: [labelAttribute, ...(extraAttributes ?? [])], filter },
    })
    setOptions(
      data.map(item => ({
        label: item[labelAttribute as keyof RecordType],
        value: item.id,
        data: item,
      })),
    )
  }

  if (!options) {
    return <Fields.Select2 label={label} options={[]} disabled />
  }

  return (
    <Form2.Select
      field={field}
      label={label}
      validations={validations}
      options={options}
      valueExtractor={(opt: any) => opt?.value}
      getOptionValue={opt => opt.value}
      getOptionLabel={opt => opt.label}
      disabled={disabled}
      isClearable={isClearable}
      formatOptionLabel={formatOptionLabel ? option => formatOptionLabel(option.data) : undefined}
    />
  )
}

interface IFieldModelSelectProps {
  label?: IComponentProp<typeof Form2.Select, "label">
  model: string
  labelAttribute: string
  onChange: (value: string) => void
  value?: string
  isClearable?: boolean
  filter?: any
}

export function FieldModelSelect({
  labelAttribute,
  model,
  label,
  onChange,
  value,
  isClearable,
  filter,
}: IFieldModelSelectProps) {
  const skeem = window.skeem
  const [options, setOptions] = useState<any[] | null>()

  useAsyncEffect(async () => {
    await fetchData()
  }, [])

  async function fetchData() {
    const data = await skeem.fetch2<any[]>({
      model: {
        name: model,
        attributes: [labelAttribute],
        filter,
        sort: { by: labelAttribute, direction: "asc" },
      },
    })
    setOptions(data.map(item => ({ label: item[labelAttribute], value: item.id })))
  }

  if (!options) {
    return <Fields.Select2 options={[]} disabled />
  }

  return (
    <Fields.Select2
      label={label}
      options={options}
      onChange={value => onChange(value?.value)}
      value={options.find(v => v.value === value)}
      isClearable={isClearable}
    />
  )
}

interface IData {
  id?: string
  title: string
  details: string
  priority: string
  source: string | null
  type: string | null
  serialNumberText: string | null
  customer: string | null
  contact: string | null
  order: string | null
  order_item: string | null
  product_master: string | null
  assigned_to_user: string | null
  assigned_to_group: string | null
  status: ISupportTicketStatus
  resolution: string
  created_at?: string | null
  closed_on?: string | null
  branch: string | null
  custom_product_code?: string
  files: (Omit<IRecord<"files">, "id"> & { id?: string; key: string; _delete?: boolean })[]
  rmas?: { id: string }
}

export async function supportTicketMutation(
  data: IData,
  ctx: {
    skeem: IWebClient
    events: ReturnType<typeof useEvents>
    api: ReturnType<typeof useApi>
  },
  currentData?: ISupportTicket,
  closeMessage?: string,
) {
  const { events, skeem, api } = ctx
  let customerName: string | undefined = undefined
  if (data.customer) {
    const customerRecord = await skeem.fetch2<IRecord<"customers", "name">>({
      first: { name: "customers", attributes: ["name"], matches: { id: data.customer } },
    })
    if (customerRecord) {
      customerName = customerRecord.name
    } else {
      data.customer = null
    }
  }
  const mutationAttributes = {
    identifier: "1",
    title: data.title,
    customerName,
    details: data.details,
    priority: data.priority,
    source: { set: data.source ?? null },
    type: { set: data.type ?? null },
    customer: { set: data.customer ?? null },
    contact: { set: data.contact ?? null },
    serialNumberText: data.serialNumberText,
    order: { set: data.order ?? null },
    order_item: { set: data.order_item ?? null },
    product_master: { set: data.product_master ?? null },
    assigned_to_user: { set: data.assigned_to_user ?? null },
    assigned_to_group: { set: data.assigned_to_group ?? null },
    status: data.status || "open",
    resolution: currentData?.resolution ?? "",
    closed_on: currentData?.closed_on ?? null,
    custom_product_code: data?.custom_product_code ?? "",
    rmas: data.rmas?.id ? { set: data.rmas?.id } : undefined,
    files: data?.files
      .map(file => {
        if (file._delete) {
          return file.id ? { destroy: file.id } : null
        }
        if (file.id) {
          return { update: { ...file, key: undefined, _delete: undefined } }
        }
        return { create: { ...file, key: undefined, _delete: undefined } }
      })
      .filter(Boolean),
  }
  if (mutationAttributes.status === "closed") {
    mutationAttributes.closed_on = new Date().toJSON()
  }
  if (data.id) {
    // --- UPDATE ---
    delete (mutationAttributes as any).identifier
    if (closeMessage) {
      mutationAttributes.resolution = closeMessage
    }
    const record = await skeem.mutate<IRecord<"support_tickets", "identifier">>({
      support_tickets: {
        update: {
          id: data.id,
          ...mutationAttributes,
        },
      },
    })
    if (checkHasChanges(currentData, data)) {
      await events.updateRecord("support_tickets", record.id)
    }
    await handleEventsUpdate(record, events, data, currentData, closeMessage)
    return record
  } else {
    // --- CREATE ---
    mutationAttributes.identifier = (await api.nextIdentifier("supportTicket")).identifier
    if (!mutationAttributes.assigned_to_user.set) {
      mutationAttributes.assigned_to_user.set = skeem.currentSession.id ?? null
    }
    const record = await skeem.mutate<IRecord<"support_tickets", "identifier">>({
      support_tickets: {
        create: mutationAttributes,
      },
    })
    await events.createRecord("support_tickets", record.id)
    await handleEventsCreate(record, events, data)
    return record
  }
}

function checkHasChanges(originalData?: ISupportTicket, newData?: IData) {
  if (!originalData || !newData) {
    return false
  }
  const keys: (keyof ISupportTicket)[] = [
    "details",
    "order",
    "order_item",
    "priority",
    "product_master",
    "serialNumberText",
    "source",
    "title",
    "type",
  ]
  return keys.some(
    key => JSON.stringify(originalData[key]) !== JSON.stringify(newData[key as keyof IData]),
  )
}

async function handleEventsCreate(
  record: { id: string },
  events: ReturnType<typeof useEvents>,
  data: IData,
) {
  if (data.customer) {
    await events.associatedToRecord("support_tickets", record.id, {
      association: "customers",
      recordId: data.customer,
    })
  }
  if (data.contact) {
    await events.associatedToRecord("support_tickets", record.id, {
      association: "contacts",
      recordId: data.contact,
      nameAttr: "name",
    })
  }
  if (data.assigned_to_group) {
    await events.associatedToRecord("support_tickets", record.id, {
      association: "support_ticket_groups",
      recordId: data.assigned_to_group,
      nameAttr: "name",
    })
  }
  if (data.assigned_to_user) {
    await events.associatedToRecord("support_tickets", record.id, {
      association: "skeem_users",
      recordId: data.assigned_to_user,
    })
  }
  if (data.status) {
    await events.ticketStatusUpdated("support_tickets", record.id, data.status)
  }
}

async function handleEventsUpdate(
  record: { id: string },
  events: ReturnType<typeof useEvents>,
  data: IData,
  currentData?: ISupportTicket,
  closeMessage?: string,
) {
  if (data.customer && data.customer !== currentData?.customer) {
    await events.associatedToRecord("support_tickets", record.id, {
      association: "customers",
      recordId: data.customer,
    })
  }
  if (data.contact && data.contact !== currentData?.contact) {
    await events.associatedToRecord("support_tickets", record.id, {
      association: "contacts",
      recordId: data.contact,
      nameAttr: "name",
    })
  }
  if (data.assigned_to_group && data.assigned_to_group !== currentData?.assigned_to_group) {
    await events.associatedToRecord("support_tickets", record.id, {
      association: "support_ticket_groups",
      recordId: data.assigned_to_group,
      nameAttr: "name",
    })
  }
  if (data.assigned_to_user && data.assigned_to_user !== currentData?.assigned_to_user) {
    await events.associatedToRecord("support_tickets", record.id, {
      association: "skeem_users",
      recordId: data.assigned_to_user,
    })
  }
  if (data.status !== currentData?.status) {
    await events.ticketStatusUpdated("support_tickets", record.id, data.status, {
      message: closeMessage,
    })
  }
}

function useQuickNote() {
  return useConfirmation<void, string | null>(({ onResolve }) => (
    <Modal
      render={() => (
        <Form2.Form onSubmit={data => onResolve(data.note)}>
          <TextGroupMedium heading="Add a new note" />
          <Form2.TextArea field="note" label="Enter your message below" />
          <ConfirmCancelButtons
            buttonOpts={{
              confirm: { children: "Save", leftIcon: icons.save },
              cancel: { leftIcon: icons.x },
            }}
            onConfirm={"submit"}
            onCancel={() => onResolve(null)}
          />
        </Form2.Form>
      )}
      onRequestClose={() => onResolve(null)}
      isOpen
    />
  ))
}

export function useTicketClosed() {
  return useConfirmation<string | undefined | null, string | null>(({ onResolve, data }) => (
    <Modal
      render={() => (
        <Form2.Form onSubmit={data => onResolve(data.note)} initialData={{ note: data ?? "" }}>
          <TextGroupMedium
            heading="Close this ticket?"
            body={
              <P>Leave some details about your resolution for this ticket using the form below</P>
            }
          />
          <Form2.TextArea field="note" label="Your resolution" />
          <ConfirmCancelButtons
            css={css`
              display: flex;
              align-items: center;
            `}
            buttonOpts={{
              confirm: { children: "Save", leftIcon: icons.save },
              cancel: { leftIcon: icons.x },
            }}
            onConfirm={"submit"}
            onCancel={() => onResolve(null)}
            extraButtons={[
              {
                children: "Save without message",
                onClick: () => onResolve(""),
                buttonColor: "white",
                leftIcon: icons.arrowRight,
              },
            ]}
          />
        </Form2.Form>
      )}
      onRequestClose={() => onResolve(null)}
      isOpen
    />
  ))
}

async function handleCreateRMAFromTicket(data: IData) {
  let order: IRecord<"orders", "created_at" | "identifier"> | null = null
  if (data.order) {
    order = await window.skeem.fetch2<IRecord<"orders", "created_at" | "identifier">>({
      first: {
        name: "orders",
        attributes: ["created_at", "identifier"],
        matches: { id: data.order },
      },
    })
  }

  let branch: IRecord<"branches", "name"> | null = null
  if (data.branch) {
    branch = await window.skeem.fetch2<IRecord<"branches", "name">>({
      first: { name: "branches", attributes: ["name"], matches: { id: data.branch } },
    })
  }

  let contact: IRecord<"contacts", "name"> | null = null
  if (data.contact) {
    contact = await window.skeem.fetch2<IRecord<"contacts", "name">>({
      first: { name: "contacts", attributes: ["name"], matches: { id: data.contact } },
    })
  }

  let customer: IRecord<"customers", "name"> | null = null
  if (data.customer) {
    customer = await window.skeem.fetch2<IRecord<"customers", "name">>({
      first: { name: "customers", attributes: ["name"], matches: { id: data.customer } },
    })
  }

  type IItem = IRecord<"order_items", "stock_code" | "body" | "quantity"> & {
    product?: IRecord<"products", "stock_code" | "body" | "product_master">
  }
  let item: IItem | null = null
  if (data.order_item) {
    item = await window.skeem.fetch2<IItem>({
      first: {
        name: "order_items",
        attributes: [
          "stock_code",
          "body",
          "quantity",
          {
            name: "product",
            attributes: ["stock_code", "body", "product_master"],
          },
        ],
        matches: { id: data.order_item },
      },
    })
  }
  type IMaster = IRecord<"product_masters", "stock_code" | "name"> & {
    products: IRecord<"products", "stock_code" | "body" | "product_master">[]
  }
  let productMaster: IMaster | null = null
  if (data.product_master) {
    productMaster = await window.skeem.fetch2<IMaster>({
      first: {
        name: "product_masters",
        attributes: [
          "stock_code",
          "name",
          {
            name: "products",
            attributes: ["stock_code", "body", "product_master"],
            filter: { matches: { branch: data.branch } },
          },
        ],
        matches: { id: data.product_master },
      },
    })
  }
  const product = item?.product ?? productMaster?.products?.[0] ?? undefined
  const rmaItem: IRmaItem = {
    id: fakeID(),
    product: product,
    quantity: item?.quantity ?? 1,
  }
  const result: IRmaFormData = {
    additionalDetails: data.details,
    creditNumber: "",
    orderDate: order?.created_at ? new Date(order.created_at) : (null as any),
    requestType: RmaRequestType.CREDIT,
    returnReason: RmaReturnReason.FAULTY,
    returnsNumber: "",
    salesNumber: order?.identifier ?? "",
    serialNumber: "",
    status: RmaStatus.PENDING,
    technicalCaseNumber: "",
    assignedTo: undefined,
    attachments: data.files.map(x => ({ ...x, id: fakeID() })) as any,
    branch: branch as any,
    contact: contact as any,
    customer: customer as any,
    order: order as any,
    items: [rmaItem],
    supportTicket: undefined,
  }
  return result
}
